<template>
  <div
    class="main-slide"
    ref="slide-container"
    id="main-slide"
    :class="{
      'has-share-presentation': sharePresentation,
      'border-base-30 border box-content rounded-md': !isPresenting,
    }"
  >
    <div
      class="slide-container"
      id="slide-container"
      :class="{
        'is-showing-qrcode-panel': isShowingQRCodePanel
      }"
      @contextmenu.prevent="handleCanvasRightClick($event)"
    >
      <QuestionImageButton v-if="slideActive.activeTab !== 2" class="hidden" />
      <CanvasRightClickMenu
        :targetX="targetX"
        :targetY="targetY"
        :offsetX="offsetX"
        :offsetY="offsetY"
        :layerX="layerX"
        :layerY="layerY"
        :targetClick="targetClick"
      />
      <ContextMenu
        :show-context-menu="showContextMenu"
        @hideFullScreen="closeContextMenu"
        @hideMenu="closeContextMenu"
        :isShowingQRCodePanel="isShowingQRCodePanel"
        v-if="!sharePresentation"
      />
      <transition
        name="qr-code-panel"
        enter-active-class="animate__animated animate__slideInLeft animate__faster"
        leave-active-class="animate__animated animate__slideOutLeft animate__faster"
      >
        <QRcodePanel v-if="$store.state.isQRCodeVisible && isPresentingOrBackstage" />
      </transition>
      <div
        class="backdrop"
        :class="{
          'is-showing-qr-code-panel': isShowingQRCodePanel
        }"
        v-if="showContextMenu"
        @mouseenter.stop="closeContextMenu()"
      />
      <div
        class="content-presenting"
        :class="{
          'show-control': isShowingControls,
          'is-showing-qrcode-panel': isShowingQRCodePanel
        }"
        :style="{'color': slideActive.textColour }"
      >
        <CanvasTransitionBackgroundImage />
        <CanvasPreview v-if="!$store.state.isTouchScreen" id="right-side" />
        <CanvasChooseASlideType v-if="isChooseASlideTypeVisible" />
        <Slide />
        <CanvasLeaderboardMusic />
        <CanvasArrowsNextBackSlide
          @selectPrevSlide="selectPrevSlide"
          @selectNextSlide="selectNextSlide"
        />
        <div
          class="canvas-pill"
          :class="{'white':isTextColourLight,
                   'no-background': sharePresentation || !isPresenting,
                   'has-chat': isLiveChatVisible}"
        >
          <div
            class="slide-menu-toggle__wrapper"
            @click.stop="openContextMenu()"
            @mouseenter.stop="openContextMenu()"
            v-show="isPresenting"
            v-if="!sharePresentation"
          >
            <div class="slide-menu-toggle horizontal layout center">
              <i class="fas fa-bars" />
              <span class="slide-menu-toggle-title">Menu</span>
            </div>
          </div>

          <LiveChat
            class="live-chat__wrapper"
            v-if="isLiveChatVisible"
            :openByDefault="isChatOpenByDefault"
          />
          <EffectMenuToogle
            class="effect-menu-toggle__wrapper"
            :class="{
              'has-chat': isLiveChatVisible,
              'no-chat': !isLiveChatVisible
            }"
            v-show="isPresenting"
            v-if="!isSharingView"
          />
        </div>

        <div class="show-qna-session" v-if="$store.state.showQnaSession">
          <div class="box-qna-session">
            <QaSession />
          </div>
        </div>
      </div>
      <VoiceCall v-if="voiceCall.isOpen" />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Hammer from 'hammerjs'
import EventBus from '@/eventBus'
import utils from '@/util'
import configs from '@/config'
import QuizStatuses from '@/constant/QuizStatuses'
import constants from '@/constant'
import Socket from '@/socket'
import storageKeys from '@/constant/storageKeys'
import ToggleQRCodeMixin from '@/mixins/presentation/ToggleQRCodeMixin'

// components
import ContextMenu from '@/components/presentations/ContextMenu.vue'
import Slide from '@/components/presentations/Slide.vue'
import CanvasPreview from '@/components-v2/common/canvas-preview/CanvasPreview.vue'
import CanvasChooseASlideType from '@/components-v2/common/canvas-preview/CanvasChooseASlideType.vue'
import CanvasArrowsNextBackSlide from '@/components-v2/presentation-editor/middle/CanvasArrowsNextBackSlide.vue'
import CanvasTransitionBackgroundImage from '@/components-v2/presentation-editor/middle/CanvasTransitionBackgroundImage.vue'
import QRcodePanel from '@/components-v2/presentation-editor/middle/QRcodePanel.vue'
import CanvasLeaderboardMusic from '@/components-v2/presentation-editor/middle/CanvasLeaderboardMusic.vue'
import CanvasRightClickMenu from '@/components-v2/presentation-editor/middle/right-click-menu/CanvasRightClickMenu.vue'
import LiveChat from '@/components-v2/presentation-editor/live-chat/LiveChat.vue'
import EffectMenuToogle from '@/components-v2/presentation-editor/middle/effects/EffectMenuToggle.vue'
import QaSession from '@/components/presentations/QaSession.vue'
import VoiceCall from '@/components-v2/voice-audio/VoiceCall.vue'

import {
  SET_CURRENT_SLIDE_MODE,
  FULL_SCREEN_MODE,
  WINDOWED_MODE,
  EDIT_MODE,
} from '@/vuex/types'
import gaCategories from '@/constant/gaCategories'
import QuestionImageButton from '../side-right/QuestionImageButton.vue'

const tracker = utils
  .gaTrackEventFactory({
    eventCategory: configs.US_PRESENTING_MODE,
    eventLabel: configs.LABEL_USER_INTERACTION,
  })
  .bind(utils)

const MAX_RIGHT_CLICK_VALUE = 3

export default {
  components: {
    ContextMenu,
    Slide,
    CanvasPreview,
    CanvasChooseASlideType,
    CanvasArrowsNextBackSlide,
    CanvasTransitionBackgroundImage,
    CanvasLeaderboardMusic,
    CanvasRightClickMenu,
    LiveChat,
    EffectMenuToogle,
    QaSession,
    VoiceCall,
    QRcodePanel,
    QuestionImageButton,
  },
  mixins: [ToggleQRCodeMixin],
  data() {
    return {
      showContextMenu: false,
      isShowingControls: false,
      targetX: null,
      targetY: null,
      offsetX: null,
      offsetY: null,
      layerX: null,
      layerY: null,
      targetClick: null,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      isPlayDemoMode: (state) => state.isPlayDemoMode,
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
      sharePresentation: (state) => state.sharePresentation,
      isNewPresentation: (state) => state.isNewPresentation,
      voiceCall: (state) => state.voiceCall,
      isQRCodeVisible: (state) => state.isQRCodeVisible,
    }),
    ...mapGetters('presentationChat', ['isFocusingChatInput']),
    ...mapGetters(['isPresenting', 'isBackstage']),

    isChooseASlideTypeVisible() {
      return this.isNewPresentation || !this.slideActive.type
    },
    isTextColourLight() {
      return this.slideActive.textColour === '#ffffff'
    },
    isQuizSlide() {
      const { type, quizStatus } = this.slideActive
      return type === 'pickAnswer' && (
        quizStatus === QuizStatuses.LOBBY_SCREEN ||
        quizStatus === QuizStatuses.RESULT_SCREEN
      )
    },
    isLeaderboardScreen() {
      return this.slideActive.type === 'leaderboard'
    },
    isQRCodeSlide() {
      const { type } = this.slideActive
      return type === 'qrcode' && this.$store.state.presentation.slides.some((slide) => slide.type === 'pickAnswer')
    },
    isSharingView() {
      return this.$store.state.sharePresentation
    },
    isLiveChatVisible() {
      return this.presentation.isEnableChat &&
      !this.presentation.audiencePacing &&
      !this.presentation.privateMode &&
        (this.isQuizSlide ||
        this.isLeaderboardScreen ||
        this.isQRCodeSlide) &&
        this.isPresenting &&
        !this.isSharingView
    },

    isChatOpenByDefault() {
      const { type, quizStatus } = this.slideActive
      return (type === 'pickAnswer' &&
        quizStatus === QuizStatuses.LOBBY_SCREEN) ||
        type === 'qrcode'
    },

    hasPrevSlide() {
      return this.$store.getters.currentSlideIndex > 0
    },
    hasNextSlide() {
      return (
        this.$store.getters.currentSlideIndex <
        this.$store.state.presentation.slides.length - 1
      )
    },
    isShowingQRCodePanel() {
      return this.isPresenting && this.isQRCodeVisible
    },
    isPresentingOrBackstage() {
      return this.isPresenting || this.isBackstage
    },
    isContentSlideType() {
      return this.slideActive.type === 'freestyle'
    },
  },
  watch: {
    isPresenting() {
      if (!this.isPresenting) this.showContextMenu = false
    },
  },
  mounted() {
    this.handleSwipeEvent()

    EventBus.$on('context-menu: update showContextMenu', (value) => {
      this.showContextMenu = value
    })
    EventBus.$on('next-back-slide-arrows: update isShowingControls', (value) => {
      this.isShowingControls = value
    })
    EventBus.$on('onKeyboard', (e) => {
      this.handleSlideControlsKeydown(e)
    })
    EventBus.$on('eventHandleNextSlide', (type) => {
      this.$emit('handleNextSlideWhenPlayingQuiz', type)
    })
  },
  destroyed() {
    EventBus.$off('context-menu: update showContextMenu')
    EventBus.$off('next-back-slide-arrows: update isShowingControls')
    EventBus.$off('onKeyboard')
    EventBus.$off('eventHandleNextSlide')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setCurrentUserAction']),
    handleSwipeEvent() {
      const hammertime = new Hammer(this.$refs['slide-container'])
      hammertime.on('swipe', (ev) => {
        if (!this.isPresenting && !this.isBackstage) return
        if (ev.direction === Hammer.DIRECTION_RIGHT) {
          if (this.hasPrevSlide) this.selectPrevSlide('swipe')
        } else {
          tracker({ eventAction: 'User swiped to next slide' })
          if (this.hasNextSlide) this.selectNextSlide('swipe')
        }
      })
    },

    openContextMenu() {
      if (!this.isPresenting) return
      this.showContextMenu = true
    },

    closeContextMenu() {
      this.showContextMenu = false
    },
    selectPrevSlide(mode) {
      this.$emit('selectPrevSlide', mode)
    },
    selectNextSlide(mode) {
      this.$emit('selectNextSlide', mode)
    },
    isFocusingInput() {
      return this.isFocusingChatInput || this.slideActive.isFocusingSlideNoteInput
    },

    handleSlideControlsKeydown(e) {
      if (!this.isPresenting && !this.isBackstage) return

      // If popup in visible in leaderboard, do nothing
      if (this.slideActive.type === 'leaderboard'
       && this.slideActive.visiblePopup) return
      // If chatbox input is focusing, and the key is not Esc, then do nothing
      if (this.isFocusingInput() && !configs.EXIT_KEYS.includes(e.code)) return

      if (this.slideActive.type == 'pickAnswer' && this.$store.state.quizConfirmation && !this.sharePresentation) {
        if (this.$store.state.historyKeyboard == e.code) {
          this.$emit('handleNextSlideWhenPlayingQuiz', this.$store.state.quizConfirmationType)
        } else {
          this.$store.state.quizConfirmation = null
        }
        return
      }

      this.$store.state.historyKeyboard = e.code
      this._handleControlKeydown(e)
      if (this.slideActive.type === 'bulletPoints') this._handleNextBackBulletPointsIndex(e)
    },

    _handleControlKeydown(e) {
      if (configs.NEXT_SLIDE_KEYS.includes(e.code)) {
        if (
          this.slideActive.type == 'pickAnswer' &&
          this.slideActive.quizStatus == constants.QuizStatuses.LOBBY_SCREEN &&
          !this.sharePresentation
        ) {
          EventBus.$emit('start-quiz')
          return
        }
        this.selectNextSlide('keyboard')
      }
      if (configs.LAST_SLIDE_KEYS.includes(e.code)) {
        this.selectPrevSlide('keyboard')
      }
      if (configs.EXIT_KEYS.includes(e.code)) {
        if (!e.shiftKey) {
          EventBus.$emit('close-backstage-window')
        }
        if (this.sharePresentation || this.isPlayDemoMode) {
          return
        }
        if (this.isQRCodeVisible) {
          this.handleToggleQrCode()
          return
        }
        if (this.$store.getters.isFullScreenMode) {
          tracker({
            eventAction: 'User switched to normal mode from full screen mode',
          })

          this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
        }
        if (this.$store.getters.isWindowedMode) {
          tracker({
            eventAction: 'User switched to edit mode from presenting mode',
          })
          this.$store.dispatch(SET_CURRENT_SLIDE_MODE, EDIT_MODE)

          // TODO:
          // - Create "start" "stop" presentation API and move the scope below to server side
          // - There is duplicated code in ContextMenu.vue (stopPresenting), move it to server side too.
          if (this.slideActive.type == 'pickAnswer') {
            if (!this.$store.state.isAdmin) {
              this.$http.put(
                configs.api.updateSlide + this.slideActive.id,
                {
                  quizStatus: constants.QuizStatuses.LOBBY_SCREEN,
                  socketId: Socket.getSocketId(),
                  accessCode: this.presentation.uniqueAccessCode,
                  activeSlide: this.slideActive.id,
                  presentationId: this.presentation.id,
                  presenting: false,
                },
              ).then(() => {}, () => {
                this.$store.dispatch('setSyncPresentation', 3)
              })
            }
          }

          if (this.presentation.id && !this.$store.state.isAdmin && !this.sharePresentation) {
            const idLastSlide = utils.getLastSlide(
              this.$store.state.presentation.slides,
            )
            const payload = {
              presenting: false,
              socketId: Socket.getSocketId(),
              activeSlidePreseting: this.slideActive.id,
              activeSlideAccessCode: this.presentation.uniqueAccessCode,
            }
            if (
              idLastSlide &&
              idLastSlide != this.$store.state.presentation.lastSlide
            ) {
              payload.lastSlide = idLastSlide
              this.$store.state.presentation.lastSlide = idLastSlide
            }
            this.$http
              .put(
                configs.api.updatePresentation + this.$store.state.presentation.id,
                payload,
              )
              .then(() => {}, () => {
                this.$store.dispatch('setSyncPresentation', 3)
              })
          }
          // END OF TODO

          this.$router.replace({
            name: 'PresentationDetail',
            params: { id: this.presentation.id },
          })
        }
      }
      if (
        configs.ENTER_FULL_SCREEN_KEYS.includes(e.code) &&
        this.$store.getters.isWindowedMode
      ) {
        tracker({
          eventAction: 'User switched to full screen mode using hotkeys',
        })
        this.$store.dispatch(SET_CURRENT_SLIDE_MODE, FULL_SCREEN_MODE)
      }
    },

    handleToggleQrCode() {
      this.toggleQrCode('PressEsc') // ToggleQRCodeMixin
    },

    _handleNextBackBulletPointsIndex(e) {
      if (e.key == 'ArrowDown') {
        if (
          this.slideActive.SlideOptions.length <=
            this.slideActive.bulletPointsIndex
        ) return
        EventBus.$emit('onKeyboard', {
          code: 'ArrowRight',
        })
      } else if (e.key == 'ArrowUp') {
        if (
          !this.slideActive.bulletPointsIndex ||
            this.slideActive.bulletPointsIndex < 1
        ) {
          return
        }
        EventBus.$emit('onKeyboard', {
          code: 'ArrowLeft',
        })
      }
    },

    handleCanvasRightClick(event) {
      if (!this.slideActive.id) return
      event.stopPropagation()
      if (this.isPresentingOrBackstage) return
      if (event.target.closest('.element-editor') || event.target.closest('.mce-editor')) return
      if (this.isContentSlideType) EventBus.$emit('content-slide-right-click-menu: hide')
      if (event) {
        this.targetX = event.x
        this.targetY = event.y
        this.offsetX = event.offsetX
        this.offsetY = event.offsetY
        this.layerX = event.layerX
        this.layerY = event.layerY
        this.targetClick = event.target
      } else {
        this.targetX = null
        this.targetY = null
        this.offsetX = null
        this.offsetY = null
        this.layerX = null
        this.layerY = null
        this.targetClick = null
      }
      this._addNumberOfClickToLocalValue()
      EventBus.$emit('canvas-right-click-menu: show')
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        'Canvas > Right click > Open canvas context menu',
      )
    },

    _addNumberOfClickToLocalValue() {
      const key = storageKeys.CANVAS_RIGHT_CLICK
      let numberOfClick = localStorage.getItem(`${this.currentUser.id}-${key}` || 0)
      numberOfClick++
      if (numberOfClick > MAX_RIGHT_CLICK_VALUE) {
        this.$store.state.isRightClickInfoBoxVisible = false
      } else {
        localStorage.setItem(`${this.currentUser.id}-${key}`, numberOfClick)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.slide-container {
  transform-origin: top left;
  overflow: hidden;
  background-color: #fff;
  transform: translateZ(0);
  -webkit-font-smoothing: subpixel-antialiased;
  backface-visibility: hidden;
  -webkit-box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.25);
  -moz-box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.25);
  box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
  transition: background-color 0.3s ease;
  &.is-showing-qrcode-panel {
  background-color: #000;

  }
}
.backdrop {
  position: fixed;
  width: 80%;
  height: 100%;
  z-index: 999;
  top: 0;
  left: 21%;
  &.is-showing-qr-code-panel {
    transform: translate(20%);
  }
}
.content-presenting {
  position: relative;
  height: 100%;
  transition: transform 0.5s ease;

  &.show-control {
    &::v-deep .slide-control {
      opacity: 1;
    }
    &::v-deep {
      .quiz-music-toggle, .escape-presenting, .private-mode, .freestyle-audio-toggle-container {
        opacity: 1;
      }
    }
  }
  &.is-showing-qrcode-panel {
    transform: translateX(10%) scale(0.8);
    overflow: hidden;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
}
.canvas-pill {
  position: relative;
  bottom: 4rem;
  height: 45px;

  &::after {
    background: rgba(255, 255, 255, 0.5);
    border-radius: 27px;
    display: block;
    content: "";
    position: absolute;
    left: 1rem;
    top: 0;
    width: 12%;
    height: 100%;
    z-index: 1;
  }

  &.white {
    &::after {
      background: rgba(0, 0, 0, 0.36);
    }
  }

  &.no-background {
    &::after {
      background: transparent;
      backdrop-filter: unset;
    }
  }

  .is-qrcode-visible {
    z-index: 19;
  }

  &.has-chat {
    &::after {
      width: 19.5%;
    }
  }
}
.live-chat__wrapper {
  position: absolute;
  cursor: pointer;
  font-size: 20px;
  z-index: 30;
  left: 8.25rem;
  top: 0.5rem;
  transition: all 0.2s ease;
}
.effect-menu-toggle__wrapper {
  cursor: pointer;
  font-size: 20px;
  z-index: 30;
  transition: all 0.2s ease;
  position: absolute;
  top: 0.65rem;
  &.has-chat {
    left: 13.75rem;
  }
  &.no-chat {
    left: 8rem;
  }
}
.show-qna-session {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 25;
  padding: 64px;

  .box-qna-session {
    width: 100%;
    height: 100%;
    border-radius: 4px;
  }

  @media (max-width: 768px) and (orientation: landscape) {
    padding-top: 86px;
    padding-bottom: 42px;
  }
}
</style>
